import React from 'react';
import Input from '../../Input/Input';
import Dropdown from '../../Dropdown/Dropdown';
import Button from '../../Button/Button';
import Checkbox from '../../Checkbox/Checkbox';
import {
  IIncentiveParamsAllOfferedClasses,
  IIncentiveParamsDogsPerShow,
  IIncentiveParamsNumberOfEntries,
  IIncentiveParamsNumberOfShows
} from '@eagerdog/interfaces';

interface IncentiveDynamicListProps {
  type: 'entries' | 'shows' | 'dogs' | 'all_offered_classes';
  items: (
    IIncentiveParamsNumberOfEntries |
    IIncentiveParamsNumberOfShows |
    IIncentiveParamsDogsPerShow |
    IIncentiveParamsAllOfferedClasses
  )[];
  setItems: (v: any[]) => void;
  showElementsWithLevels: Record<string, string[]>;
  availableShowTypes: Record<string, unknown>;
  enableEntryClassOverrideConditions?: boolean;
}

const IncentiveDynamicList: React.FC<IncentiveDynamicListProps> = ({
                                                                     type,
                                                                     items,
                                                                     setItems,
                                                                     showElementsWithLevels,
                                                                     availableShowTypes,
                                                                     enableEntryClassOverrideConditions = false,
                                                                   }) => {
  const [decimalInputs, setDecimalInputs] = React.useState<Record<string, string>>({});

  const getDecimalValue = (key: string, fallback?: number | string) => {
    if (decimalInputs[key] !== undefined) {
      return decimalInputs[key];
    }
    if (fallback === undefined || fallback === null) {
      return "";
    }
    return fallback.toString();
  };

  const setDecimalValue = (key: string, value: string) => {
    setDecimalInputs((prev) => ({ ...prev, [key]: value }));
  };

  const addMain = () => {
    const base =
      type === 'entries'
        ? { fee: 0, number_of_entries: 0, show_type: '' }
        : type === 'shows'
          ? { fee: 0, number_of_shows: 0, show_type: '' }
          : type === 'all_offered_classes'
            ? { deduction: 0, show_type: '' }
            : { fee: 0, number_of_dogs: 0, show_type: '' };
    setItems([...items, base]);
  };

  const addException = (index: number) => {
    const updated = [...items];
    if (!updated[index].exceptions) updated[index].exceptions = [];
    const baseException: any = type === 'all_offered_classes'
      ? { show_element: '', deduction: 0 }
      : { show_element: '', fee: 0 };
    updated[index].exceptions.push(baseException);
    setItems(updated);
  };

  const addQualifyingClass = (index: number) => {
    const updated = [...items];
    const item = updated[index] as IIncentiveParamsNumberOfShows;
    if (!item.qualifying_classes) item.qualifying_classes = [];
    item.qualifying_classes.push({ show_element: '' });
    setItems(updated);
  };

  const deleteQualifyingClass = (i: number, qcIndex: number) => {
    const updated = [...items];
    (updated[i] as IIncentiveParamsNumberOfShows).qualifying_classes!.splice(qcIndex, 1);
    setItems(updated);
  };

  const addBundleClass = (index: number) => {
    const updated = [...items];
    const item = updated[index] as IIncentiveParamsAllOfferedClasses;
    if (!item.qualifying_classes) item.qualifying_classes = [];
    item.qualifying_classes.push({ show_element: '' });
    setItems(updated);
  };

  const deleteBundleClass = (i: number, qcIndex: number) => {
    const updated = [...items];
    const item = updated[i] as IIncentiveParamsAllOfferedClasses;
    item.qualifying_classes!.splice(qcIndex, 1);
    setItems(updated);
  };

  const deleteMain = (index: number) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
  };

  const deleteException = (i: number, exIndex: number) => {
    const updated = [...items];
    updated[i].exceptions!.splice(exIndex, 1);
    setItems(updated);
  };

  return (
    <>
      {items.map((element, index) => (
        <div key={index}>
          <div className="feePercentageRow">
            <Dropdown
              label="Show Type"
              placeholder="Show Type"
              options={Object.entries(availableShowTypes).map(([key]: [string, unknown]) => ({ id: key, value: key }))}
              value={{
                id: (element as IIncentiveParamsNumberOfShows).show_type,
                value: (element as IIncentiveParamsNumberOfShows).show_type
              }}
              onChange={(e, val) => {
                const updated = [...items];
                (updated[index] as IIncentiveParamsNumberOfShows).show_type = val.id;
                setItems(updated);
              }}
            />

            {type !== 'all_offered_classes' && (
              <Input
                value={
                  type === 'entries'
                    ? (element as IIncentiveParamsNumberOfEntries).number_of_entries
                    : type === 'shows'
                      ? (element as IIncentiveParamsNumberOfShows).number_of_shows
                      : (element as IIncentiveParamsDogsPerShow).number_of_dogs
                }
                label={
                  type === 'entries'
                    ? 'Number of Entries'
                    : type === 'shows'
                      ? 'Number of Shows'
                      : 'Number of Dogs'
                }
                type="number"
                step="1"
                placeholder={
                  type === 'entries'
                    ? 'Set Number of entries'
                    : type === 'shows'
                      ? 'Set Number of shows'
                      : 'Set Number of dogs'
                }
                onChange={(e) => {
                  e.stopPropagation();
                  const updated = [...items];
                  const val = parseFloat(e.target.value);
                  if (type === 'entries') {
                    (updated[index] as IIncentiveParamsNumberOfEntries).number_of_entries = val;
                  } else if (type === 'shows') {
                    (updated[index] as IIncentiveParamsNumberOfShows).number_of_shows = val;
                  } else {
                    (updated[index] as IIncentiveParamsDogsPerShow).number_of_dogs = val;
                  }
                  setItems(updated);
                }}
              />
            )}

            {type === 'all_offered_classes' ? (
              <>
                <Input
                  value={getDecimalValue(`deduction-${index}`, (element as IIncentiveParamsAllOfferedClasses).deduction)}
                  label="Deduction"
                  type="number"
                  step="any"
                  placeholder="Amount to deduct per qualifying class"
                  onChange={(e) => {
                    e.stopPropagation();
                    const nextValue = e.target.value;
                    setDecimalValue(`deduction-${index}`, nextValue);
                    const parsed = parseFloat(nextValue);
                    if (!Number.isNaN(parsed)) {
                      const updated = [...items];
                      (updated[index] as IIncentiveParamsAllOfferedClasses).deduction = parsed;
                      setItems(updated);
                    }
                  }}
                  onBlur={() => {
                    const raw = getDecimalValue(`deduction-${index}`, (element as IIncentiveParamsAllOfferedClasses).deduction);
                    if (raw.trim().length === 0) { setDecimalValue(`deduction-${index}`, ""); return; }
                    const parsed = parseFloat(raw);
                    if (!Number.isNaN(parsed)) {
                      const updated = [...items];
                      (updated[index] as IIncentiveParamsAllOfferedClasses).deduction = parsed;
                      setItems(updated);
                      setDecimalValue(`deduction-${index}`, parsed.toString());
                    }
                  }}
                />
                <Input
                  value={getDecimalValue(`junior-deduction-${index}`, (element as IIncentiveParamsAllOfferedClasses).junior_deduction ?? "")}
                  label="Junior Deduction"
                  type="number"
                  step="any"
                  placeholder="Optional"
                  onChange={(e) => {
                    e.stopPropagation();
                    const nextValue = e.target.value;
                    setDecimalValue(`junior-deduction-${index}`, nextValue);
                    const parsed = parseFloat(nextValue);
                    if (!Number.isNaN(parsed)) {
                      const updated = [...items];
                      (updated[index] as IIncentiveParamsAllOfferedClasses).junior_deduction = parsed;
                      setItems(updated);
                    }
                  }}
                  onBlur={() => {
                    const raw = getDecimalValue(`junior-deduction-${index}`, (element as IIncentiveParamsAllOfferedClasses).junior_deduction ?? "");
                    if (raw.trim().length === 0) { setDecimalValue(`junior-deduction-${index}`, ""); return; }
                    const parsed = parseFloat(raw);
                    if (!Number.isNaN(parsed)) {
                      const updated = [...items];
                      (updated[index] as IIncentiveParamsAllOfferedClasses).junior_deduction = parsed;
                      setItems(updated);
                      setDecimalValue(`junior-deduction-${index}`, parsed.toString());
                    }
                  }}
                />
              </>
            ) : (
              <>
                <Input
                  value={getDecimalValue(`fee-${index}`, (element as IIncentiveParamsNumberOfEntries).fee)}
                  label="Fee"
                  type="number"
                  step="any"
                  placeholder="Set Fee"
                  onChange={(e) => {
                    e.stopPropagation();
                    const nextValue = e.target.value;
                    setDecimalValue(`fee-${index}`, nextValue);
                    const parsed = parseFloat(nextValue);
                    if (!Number.isNaN(parsed)) {
                      const updated = [...items];
                      (updated[index] as IIncentiveParamsNumberOfEntries).fee = parsed;
                      setItems(updated);
                    }
                  }}
                  onBlur={() => {
                    const raw = getDecimalValue(`fee-${index}`, (element as IIncentiveParamsNumberOfEntries).fee);
                    if (raw.trim().length === 0) { setDecimalValue(`fee-${index}`, ""); return; }
                    const parsed = parseFloat(raw);
                    if (!Number.isNaN(parsed)) {
                      const updated = [...items];
                      (updated[index] as IIncentiveParamsNumberOfEntries).fee = parsed;
                      setItems(updated);
                      setDecimalValue(`fee-${index}`, parsed.toString());
                    }
                  }}
                />
                <Input
                  value={getDecimalValue(`junior-${index}`, (element as IIncentiveParamsNumberOfEntries).junior_fee ?? "")}
                  label="Junior Fee"
                  type="number"
                  step="any"
                  placeholder="Set Junior Fee (not required)"
                  onChange={(e) => {
                    e.stopPropagation();
                    const nextValue = e.target.value;
                    setDecimalValue(`junior-${index}`, nextValue);
                    const parsed = parseFloat(nextValue);
                    if (!Number.isNaN(parsed)) {
                      const updated = [...items];
                      (updated[index] as IIncentiveParamsNumberOfEntries).junior_fee = parsed;
                      setItems(updated);
                    }
                  }}
                  onBlur={() => {
                    const raw = getDecimalValue(`junior-${index}`, (element as IIncentiveParamsNumberOfEntries).junior_fee ?? "");
                    if (raw.trim().length === 0) { setDecimalValue(`junior-${index}`, ""); return; }
                    const parsed = parseFloat(raw);
                    if (!Number.isNaN(parsed)) {
                      const updated = [...items];
                      (updated[index] as IIncentiveParamsNumberOfEntries).junior_fee = parsed;
                      setItems(updated);
                      setDecimalValue(`junior-${index}`, parsed.toString());
                    }
                  }}
                />
              </>
            )}

            <Button type="button" onClick={() => addException(index)}>
              + Add Exception
            </Button>

            {type === 'shows' && (
              <Button type="button" onClick={() => addQualifyingClass(index)}>
                + Add Qualifying Class
              </Button>
            )}

            {type === 'all_offered_classes' && (
              <Button type="button" onClick={() => addBundleClass(index)}>
                + Define Bundle
              </Button>
            )}

            <div className="btnWrap">
              <button onClick={() => deleteMain(index)} className="delete" type="button">
                Delete Element
              </button>
            </div>
          </div>

          {type === 'shows' && (
            <div className="consistentClassCountRow">
              <Checkbox
                id={`consistent-class-count-${index}`}
                label="Require consistent class count across all trials (e.g. all single-class, all Double-Q, or all Triple-Q — mixing disqualifies)"
                value={!!(element as IIncentiveParamsNumberOfShows).require_consistent_class_count}
                onChange={() => {
                  const updated = [...items];
                  const current = !!(updated[index] as IIncentiveParamsNumberOfShows).require_consistent_class_count;
                  (updated[index] as IIncentiveParamsNumberOfShows).require_consistent_class_count = !current;
                  setItems(updated);
                }}
              />
            </div>
          )}

          {type === 'shows' && (() => {
            const showsItem = element as IIncentiveParamsNumberOfShows;
            const qcs = showsItem.qualifying_classes || [];
            if (qcs.length === 0) return null;
            const parentShowType = showsItem.show_type;
            const showTypeElements: Record<string, string[]> | undefined = parentShowType
              ? (availableShowTypes as any)[parentShowType]
              : undefined;
            const showElementOptions = (() => {
              if (!showTypeElements) {
                return Object.keys(showElementsWithLevels).map((el) => ({ id: el, value: el }));
              }
              const typeKeys = new Set(Object.keys(showTypeElements));
              return Object.keys(showElementsWithLevels)
                .filter((k) => {
                  const levels = showElementsWithLevels[k] || [];
                  return levels.some((lvl) => typeKeys.has(lvl)) || typeKeys.has(k);
                })
                .map((el) => ({ id: el, value: el }));
            })();
            return (
              <div className="qualifyingClassGroup">
                <div className="qualifyingClassLabel">Qualifying Classes — only shows where the dog enters one of these classes count toward the tier total</div>
                {qcs.map((qc, qcIndex) => {
                  const lvlOptions = qc.show_element
                    ? (showElementsWithLevels[qc.show_element] || []).map((lvl) => ({ id: lvl, value: lvl }))
                    : [];
                  return (
                    <div key={qcIndex} className="qualifyingClassRow">
                      <Dropdown
                        label="Show Element"
                        placeholder="Show Element"
                        options={showElementOptions}
                        value={{ id: qc.show_element, value: qc.show_element }}
                        onChange={(e, val) => {
                          const updated = [...items];
                          (updated[index] as IIncentiveParamsNumberOfShows).qualifying_classes![qcIndex].show_element = val.id;
                          (updated[index] as IIncentiveParamsNumberOfShows).qualifying_classes![qcIndex].level = '';
                          setItems(updated);
                        }}
                      />
                      {qc.show_element && (
                        <Dropdown
                          label="Level"
                          placeholder="Level (optional)"
                          options={[{ id: '', value: 'Any' }, ...lvlOptions]}
                          value={{ id: qc.level || '', value: qc.level || 'Any' }}
                          onChange={(e, val) => {
                            const updated = [...items];
                            (updated[index] as IIncentiveParamsNumberOfShows).qualifying_classes![qcIndex].level = val.id || undefined;
                            setItems(updated);
                          }}
                        />
                      )}
                      <div className="btnWrap">
                        <button onClick={() => deleteQualifyingClass(index, qcIndex)} className="delete" type="button">
                          Delete Qualifying Class
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          <div className="exceptionGroup">
            {type === 'all_offered_classes' && (() => {
              const aocItem = element as IIncentiveParamsAllOfferedClasses;
              const bcs = aocItem.qualifying_classes || [];
              if (bcs.length === 0) return null;
              const parentShowType = (aocItem as any).show_type;
              const showTypeElements: Record<string, string[]> | undefined = parentShowType
                ? (availableShowTypes as any)[parentShowType]
                : undefined;
              const showElementOptions = (() => {
                if (!showTypeElements) {
                  return Object.keys(showElementsWithLevels).map((el) => ({ id: el, value: el }));
                }
                const typeKeys = new Set(Object.keys(showTypeElements));
                return Object.keys(showElementsWithLevels)
                  .filter((k) => {
                    const levels = showElementsWithLevels[k] || [];
                    return levels.some((lvl) => typeKeys.has(lvl)) || typeKeys.has(k);
                  })
                  .map((el) => ({ id: el, value: el }));
              })();
              return (
                <div className="qualifyingClassGroup">
                  <div className="qualifyingClassLabel">Bundle Classes — deduction applies only when dog enters ALL of these classes</div>
                  {bcs.map((bc, bcIndex) => {
                    const lvlOptions = bc.show_element
                      ? (showElementsWithLevels[bc.show_element] || []).map((lvl) => ({ id: lvl, value: lvl }))
                      : [];
                    return (
                      <div key={bcIndex} className="qualifyingClassRow">
                        <Dropdown
                          label="Show Element"
                          placeholder="Show Element"
                          options={showElementOptions}
                          value={{ id: bc.show_element, value: bc.show_element }}
                          onChange={(e, val) => {
                            const updated = [...items];
                            (updated[index] as IIncentiveParamsAllOfferedClasses).qualifying_classes![bcIndex].show_element = val.id;
                            (updated[index] as IIncentiveParamsAllOfferedClasses).qualifying_classes![bcIndex].level = undefined;
                            setItems(updated);
                          }}
                        />
                        {bc.show_element && (
                          <Dropdown
                            label="Level"
                            placeholder="Level (optional)"
                            options={[{ id: '', value: 'Any' }, ...lvlOptions]}
                            value={{ id: bc.level || '', value: bc.level || 'Any' }}
                            onChange={(e, val) => {
                              const updated = [...items];
                              (updated[index] as IIncentiveParamsAllOfferedClasses).qualifying_classes![bcIndex].level = val.id || undefined;
                              setItems(updated);
                            }}
                          />
                        )}
                        <div className="btnWrap">
                          <button onClick={() => deleteBundleClass(index, bcIndex)} className="delete" type="button">
                            Delete Bundle Class
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {element.exceptions?.map((ex, exIndex) => {
              const exWithConditions = ex as typeof ex & {
                min_total_entries?: number;
                requires_other_regular_entry?: boolean;
                min_shows?: number;
              };
              const parentShowType = (element as IIncentiveParamsNumberOfShows).show_type;
              const showTypeElements: Record<string, string[]> | undefined = parentShowType
                ? (availableShowTypes as any)[parentShowType]
                : undefined;

              // showElementsWithLevels is pre-computed in IncentiveForm and maps each
              // logical element name to its selectable levels. Filter it to only elements
              // that belong to the selected parent show type using two checks:
              //   1. The element's levels contain at least one key from showTypeElements
              //      (covers flat/levels-as-keys show types like UKC Obedience where
              //       showElementsWithLevels["Obedience"] = the flat level keys).
              //   2. The element key itself is a direct key in showTypeElements
              //      (covers nested elements like "Non-Licensed Classes" or Dock Jumping "Air").
              const showElementOptions = (() => {
                if (!showTypeElements) {
                  return Object.keys(showElementsWithLevels).map((el) => ({ id: el, value: el }));
                }
                const typeKeys = new Set(Object.keys(showTypeElements));
                return Object.keys(showElementsWithLevels)
                  .filter((k) => {
                    const levels = showElementsWithLevels[k] || [];
                    return levels.some((lvl) => typeKeys.has(lvl)) || typeKeys.has(k);
                  })
                  .map((el) => ({ id: el, value: el }));
              })();

              const levelOptions = ex.show_element
                ? (showElementsWithLevels[ex.show_element] || []).map((lvl) => ({ id: lvl, value: lvl }))
                : [];

              return (
                <div key={exIndex} className="exceptionRow">
                  <Dropdown
                    label="Show Element"
                    placeholder="Show Element"
                    options={showElementOptions}
                    value={{ id: ex.show_element, value: ex.show_element }}
                    onChange={(e, val) => {
                      const updated = [...items];
                      updated[index].exceptions![exIndex].show_element = val.id;
                      updated[index].exceptions![exIndex].level = '';
                      setItems(updated);
                    }}
                  />

                  {ex.show_element && (
                    <Dropdown
                      label="Level"
                      placeholder="Level"
                      options={levelOptions}
                      value={{
                        id: ex.level || '',
                        value: ex.level || '',
                      }}
                      onChange={(e, val) => {
                        const updated = [...items];
                        updated[index].exceptions![exIndex].level = val.id;
                        setItems(updated);
                      }}
                    />
                  )}

                  {type === 'all_offered_classes' ? (
                    <>
                      <Input
                        value={getDecimalValue(`ex-deduction-${index}-${exIndex}`, (ex as any).deduction ?? "")}
                        label="Deduction"
                        type="number"
                        step="any"
                        placeholder="Set Deduction"
                        onChange={(e) => {
                          e.stopPropagation();
                          const nextValue = e.target.value;
                          setDecimalValue(`ex-deduction-${index}-${exIndex}`, nextValue);
                          const parsed = parseFloat(nextValue);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).deduction = parsed;
                            setItems(updated);
                          }
                        }}
                        onBlur={() => {
                          const raw = getDecimalValue(`ex-deduction-${index}-${exIndex}`, (ex as any).deduction ?? "");
                          if (raw.trim().length === 0) { setDecimalValue(`ex-deduction-${index}-${exIndex}`, ""); return; }
                          const parsed = parseFloat(raw);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).deduction = parsed;
                            setItems(updated);
                            setDecimalValue(`ex-deduction-${index}-${exIndex}`, parsed.toString());
                          }
                        }}
                      />
                      <Input
                        value={getDecimalValue(`ex-early-deduction-${index}-${exIndex}`, (ex as any).early_bird_deduction ?? "")}
                        label="Early Bird Deduction"
                        type="number"
                        step="any"
                        placeholder="Optional"
                        onChange={(e) => {
                          e.stopPropagation();
                          const nextValue = e.target.value;
                          setDecimalValue(`ex-early-deduction-${index}-${exIndex}`, nextValue);
                          const parsed = parseFloat(nextValue);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).early_bird_deduction = parsed;
                            setItems(updated);
                          }
                        }}
                        onBlur={() => {
                          const raw = getDecimalValue(`ex-early-deduction-${index}-${exIndex}`, (ex as any).early_bird_deduction ?? "");
                          if (raw.trim().length === 0) { setDecimalValue(`ex-early-deduction-${index}-${exIndex}`, ""); return; }
                          const parsed = parseFloat(raw);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).early_bird_deduction = parsed;
                            setItems(updated);
                            setDecimalValue(`ex-early-deduction-${index}-${exIndex}`, parsed.toString());
                          }
                        }}
                      />
                      <Input
                        value={getDecimalValue(`ex-junior-deduction-${index}-${exIndex}`, (ex as any).junior_deduction ?? "")}
                        label="Junior Deduction"
                        type="number"
                        step="any"
                        placeholder="Optional"
                        onChange={(e) => {
                          e.stopPropagation();
                          const nextValue = e.target.value;
                          setDecimalValue(`ex-junior-deduction-${index}-${exIndex}`, nextValue);
                          const parsed = parseFloat(nextValue);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).junior_deduction = parsed;
                            setItems(updated);
                          }
                        }}
                        onBlur={() => {
                          const raw = getDecimalValue(`ex-junior-deduction-${index}-${exIndex}`, (ex as any).junior_deduction ?? "");
                          if (raw.trim().length === 0) { setDecimalValue(`ex-junior-deduction-${index}-${exIndex}`, ""); return; }
                          const parsed = parseFloat(raw);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).junior_deduction = parsed;
                            setItems(updated);
                            setDecimalValue(`ex-junior-deduction-${index}-${exIndex}`, parsed.toString());
                          }
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Input
                        value={getDecimalValue(`ex-fee-${index}-${exIndex}`, (ex as any).fee)}
                        label="Fee"
                        type="number"
                        step="any"
                        placeholder="Set Fee"
                        onChange={(e) => {
                          e.stopPropagation();
                          const nextValue = e.target.value;
                          setDecimalValue(`ex-fee-${index}-${exIndex}`, nextValue);
                          const parsed = parseFloat(nextValue);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).fee = parsed;
                            setItems(updated);
                          }
                        }}
                        onBlur={() => {
                          const raw = getDecimalValue(`ex-fee-${index}-${exIndex}`, (ex as any).fee);
                          if (raw.trim().length === 0) { setDecimalValue(`ex-fee-${index}-${exIndex}`, ""); return; }
                          const parsed = parseFloat(raw);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).fee = parsed;
                            setItems(updated);
                            setDecimalValue(`ex-fee-${index}-${exIndex}`, parsed.toString());
                          }
                        }}
                      />
                      <Input
                        value={getDecimalValue(`ex-early-${index}-${exIndex}`, (ex as any).early_bird_fee ?? "")}
                        label="Early Bird Fee"
                        type="number"
                        step="any"
                        placeholder="Optional"
                        onChange={(e) => {
                          e.stopPropagation();
                          const nextValue = e.target.value;
                          setDecimalValue(`ex-early-${index}-${exIndex}`, nextValue);
                          const parsed = parseFloat(nextValue);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).early_bird_fee = parsed;
                            setItems(updated);
                          }
                        }}
                        onBlur={() => {
                          const raw = getDecimalValue(`ex-early-${index}-${exIndex}`, (ex as any).early_bird_fee ?? "");
                          if (raw.trim().length === 0) { setDecimalValue(`ex-early-${index}-${exIndex}`, ""); return; }
                          const parsed = parseFloat(raw);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).early_bird_fee = parsed;
                            setItems(updated);
                            setDecimalValue(`ex-early-${index}-${exIndex}`, parsed.toString());
                          }
                        }}
                      />
                      <Input
                        value={getDecimalValue(`ex-junior-${index}-${exIndex}`, (ex as any).junior_fee ?? "")}
                        label="Junior Fee"
                        type="number"
                        step="any"
                        placeholder="Optional"
                        onChange={(e) => {
                          e.stopPropagation();
                          const nextValue = e.target.value;
                          setDecimalValue(`ex-junior-${index}-${exIndex}`, nextValue);
                          const parsed = parseFloat(nextValue);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).junior_fee = parsed;
                            setItems(updated);
                          }
                        }}
                        onBlur={() => {
                          const raw = getDecimalValue(`ex-junior-${index}-${exIndex}`, (ex as any).junior_fee ?? "");
                          if (raw.trim().length === 0) { setDecimalValue(`ex-junior-${index}-${exIndex}`, ""); return; }
                          const parsed = parseFloat(raw);
                          if (!Number.isNaN(parsed)) {
                            const updated = [...items];
                            (updated[index].exceptions![exIndex] as any).junior_fee = parsed;
                            setItems(updated);
                            setDecimalValue(`ex-junior-${index}-${exIndex}`, parsed.toString());
                          }
                        }}
                      />
                    </>
                  )}
                  {type === "shows" && (
                    <Input
                      value={exWithConditions.min_shows ?? ""}
                      label="Min Shows (add-on pack)"
                      type="number"
                      step="1"
                      placeholder="Optional (e.g. 6)"
                      onChange={(e) => {
                        e.stopPropagation();
                        const updated = [...items];
                        const nextValue = e.target.value;
                        if (nextValue === "") {
                          delete (updated[index].exceptions![exIndex] as any).min_shows;
                        } else {
                          const parsed = parseInt(nextValue, 10);
                          if (!Number.isNaN(parsed)) {
                            (updated[index].exceptions![exIndex] as any).min_shows = parsed;
                          }
                        }
                        setItems(updated);
                      }}
                    />
                  )}
                  {type === "entries" && enableEntryClassOverrideConditions && (
                    <>
                      <Input
                        value={exWithConditions.min_total_entries ?? ""}
                        label="Min Total Entries"
                        type="number"
                        step="1"
                        placeholder="Optional (e.g. 2)"
                        onChange={(e) => {
                          e.stopPropagation();
                          const updated = [...items];
                          const nextValue = e.target.value;
                          if (nextValue === "") {
                            delete (updated[index].exceptions![exIndex] as any).min_total_entries;
                          } else {
                            const parsed = parseInt(nextValue, 10);
                            if (!Number.isNaN(parsed)) {
                              (updated[index].exceptions![exIndex] as any).min_total_entries = parsed;
                            }
                          }
                          setItems(updated);
                        }}
                      />
                      <div style={{ alignSelf: "center", minWidth: "220px", marginTop: "12px" }}>
                        <Checkbox
                          id={`ex-regular-entry-${index}-${exIndex}`}
                          label="Requires other regular entry"
                          value={!!exWithConditions.requires_other_regular_entry}
                          onChange={() => {
                            const updated = [...items];
                            const nextValue = !((updated[index].exceptions![exIndex] as any).requires_other_regular_entry);
                            (updated[index].exceptions![exIndex] as any).requires_other_regular_entry = nextValue;
                            setItems(updated);
                          }}
                        />
                      </div>
                    </>
                  )}
                  <div className="btnWrap">
                    <button onClick={() => deleteException(index, exIndex)} className="delete" type="button">
                      Delete Exception
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <hr />
        </div>
      ))}

      <div className="addFeeButton">
        <Button type="button" onClick={addMain}>
          + Add Fee
        </Button>
      </div>
    </>
  );
};

export default IncentiveDynamicList;
